<template>
  <div class="tax-savings-wrapper">
    <div v-if="data && data.length > 0">
      <v-table density="comfortable">
        <thead>
          <tr class="bg-grey-lighten-3">
            <th class="text-left font-weight-bold">Group</th>
            <th class="text-right font-weight-bold">Cap (฿)</th>
            <th class="text-right font-weight-bold">Deductible Users</th>
            <th class="text-right font-weight-bold">Deductible Amount (฿)</th>
            <th class="text-right font-weight-bold">Max Gap (฿)</th>
            <th class="text-center font-weight-bold" style="min-width: 180px">Max Gap / Cap</th>
            <th class="text-right font-weight-bold">CTR</th>
            <th class="text-left font-weight-bold products-col">Products</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="(item, index) in data" :key="index" :class="['hover-row', index % 2 === 0 ? 'row-odd' : 'row-even']">
            <td class="font-weight-medium">{{ item.group.name }}</td>
            <td class="text-right text-body-2">{{ formatNumber(item.group.cap) }}</td>
            <td class="text-right text-body-2">{{ formatNumber(item.avg_gap?.deductible_users) }}</td>
            <td class="text-right text-body-2">{{ formatNumber(item.avg_gap?.deductible_amount) }}</td>
            <td class="text-right text-body-2">{{ formatNumber(item.max_gap) }}</td>
            <td class="text-center">
              <div class="gap-bar-container">
                <div class="gap-bar">
                  <div class="gap-bar__filled" :style="{ width: getGapPercent(item) + '%' }" :class="getGapBarClass(item)"></div>
                </div>
                <span class="gap-bar__label text-body-2">{{ getGapPercent(item) }}%</span>
              </div>
            </td>
            <td class="text-right">
              <v-chip :color="getCtrColor(getCtrPercent(item))" size="small" label variant="tonal"> {{ getCtrPercent(item) }}% </v-chip>
            </td>
            <td class="text-body-2 products-col">
              <div class="products-cell align-center ga-1" :ref="'products-' + index">
                <v-chip
                  v-for="(product, pIdx) in item.products"
                  :key="product"
                  size="x-small"
                  class="product-chip h-100"
                  variant="outlined"
                  color="primary"
                  :class="{ 'product-hidden': overflowMap[index] && pIdx >= overflowMap[index].visible }"
                >
                  {{ product }}
                </v-chip>
                <v-tooltip v-if="overflowMap[index] && overflowMap[index].hidden > 0" location="top">
                  <template v-slot:activator="{ props }">
                    <v-chip v-bind="props" size="x-small" class="mr-1 mb-1 product-overflow-chip" variant="tonal" color="primary"> +{{ overflowMap[index].hidden }} </v-chip>
                  </template>
                  <div>{{ item.products.slice(overflowMap[index].visible).join(', ') }}</div>
                </v-tooltip>
              </div>
            </td>
          </tr>
        </tbody>
      </v-table>

      <!-- Summary row -->
      <div class="d-flex align-center justify-space-between mt-3 px-2 py-2 bg-grey-lighten-4 rounded">
        <div class="d-flex align-center ga-4">
          <span class="text-body-2 font-weight-medium">Total Max Gap:</span>
          <span class="text-body-2 font-weight-bold">฿{{ formatNumber(totalMaxGap) }}</span>
        </div>
        <div class="d-flex align-center ga-4">
          <span class="text-body-2 font-weight-medium">Avg CTR:</span>
          <span class="text-body-2 font-weight-bold">{{ avgCtr }}%</span>
        </div>
        <div class="d-flex align-center ga-4">
          <span class="text-body-2 font-weight-medium">Total Deductible Users:</span>
          <span class="text-body-2 font-weight-bold">{{ formatNumber(totalDeductibleUsers) }}</span>
        </div>
      </div>
    </div>

    <div v-else class="d-flex flex-column align-center justify-center" style="min-height: 200px">
      <v-icon size="48" color="grey-lighten-1">mdi-cash-multiple</v-icon>
      <span class="text-body-2 text-medium-emphasis mt-2">No tax savings data in selected period</span>
    </div>
  </div>
</template>

<script>
export default {
  name: 'TaxSavingsTable',
  props: {
    data: {
      type: Array,
      required: true,
    },
  },
  data() {
    return {
      overflowMap: {},
    };
  },
  watch: {
    data: {
      handler() {
        // Reset overflow map so all chips become visible in the DOM first
        this.overflowMap = {};
        // Wait for DOM to render all chips unhidden, then measure
        this.$nextTick(() => {
          this.$nextTick(() => this.calculateOverflow());
        });
      },
      immediate: true,
    },
  },
  mounted() {
    this.$nextTick(() => this.calculateOverflow());
  },
  computed: {
    totalMaxGap() {
      return this.data.reduce((sum, item) => sum + (item.max_gap || 0), 0);
    },
    avgCtr() {
      const totalClicks = this.data.reduce((sum, item) => sum + (item.ctr?.clicks || 0), 0);
      const totalRec = this.data.reduce((sum, item) => sum + (item.ctr?.recommendation_amount || 0), 0);
      if (totalRec === 0) return 0;
      return (Math.round((totalClicks / totalRec) * 1000) / 10).toFixed(1);
    },
    totalDeductibleUsers() {
      return this.data.reduce((sum, item) => sum + (item.avg_gap?.deductible_users || 0), 0);
    },
  },
  methods: {
    formatNumber(num) {
      return num?.toLocaleString('en-US') || '0';
    },
    getGapPercent(item) {
      if (!item.group.cap || item.group.cap === 0) return 0;
      return Math.min(Math.round((item.max_gap / item.group.cap) * 100), 100);
    },
    getGapBarClass(item) {
      const pct = this.getGapPercent(item);
      if (pct >= 50) return 'gap-bar__filled--high';
      if (pct >= 25) return 'gap-bar__filled--medium';
      return 'gap-bar__filled--low';
    },
    getCtrPercent(item) {
      const clicks = item.ctr?.clicks || 0;
      const rec = item.ctr?.recommendation_amount || 0;
      if (rec === 0) return 0;
      return (Math.round((clicks / rec) * 1000) / 10).toFixed(1);
    },
    getCtrColor(pct) {
      const val = parseFloat(pct);
      if (val >= 20) return 'success';
      if (val >= 10) return 'warning';
      if (val > 0) return 'info';
      return 'grey';
    },
    calculateOverflow() {
      const map = {};
      this.data.forEach((item, index) => {
        const container = this.$refs['products-' + index];
        const el = Array.isArray(container) ? container[0] : container;
        if (!el) {
          map[index] = { visible: item.products.length, hidden: 0 };
          return;
        }
        const chips = el.querySelectorAll('.product-chip');
        if (chips.length === 0) {
          map[index] = { visible: item.products.length, hidden: 0 };
          return;
        }
        // Get the top of the first chip to determine line positions
        const firstTop = chips[0].offsetTop;
        const lineHeight = chips[0].offsetHeight + 4; // chip height + margin
        const maxBottom = firstTop + lineHeight * 2; // allow 2 lines

        let visibleCount = item.products.length;
        for (let i = 0; i < chips.length; i++) {
          if (chips[i].offsetTop >= maxBottom) {
            visibleCount = i;
            break;
          }
        }
        map[index] = { visible: visibleCount, hidden: item.products.length - visibleCount };
      });
      this.overflowMap = map;
    },
  },
};
</script>

<style scoped>
.tax-savings-wrapper {
  width: 100%;
}

.hover-row {
  transition: background-color 0.2s ease;
}

.row-odd {
  background-color: rgba(0, 0, 0, 0.03);
}

.row-even {
  background-color: rgba(255, 255, 255, 0.5);
}

.hover-row:hover {
  background-color: rgba(0, 0, 0, 0.06) !important;
}

thead tr th {
  font-size: 0.75rem;
  padding: 14px 8px;
  border-bottom: 2px solid rgba(0, 0, 0, 0.12);
  white-space: nowrap;
}

tbody tr td {
  padding: 16px 8px;
  font-size: 0.813rem;
  word-break: break-word;
}

.gap-bar-container {
  display: flex;
  align-items: center;
  gap: 8px;
}

.gap-bar {
  flex: 1;
  height: 8px;
  background-color: #e0e0e0;
  border-radius: 4px;
  overflow: hidden;
}

.gap-bar__filled {
  height: 100%;
  border-radius: 4px;
  transition: width 0.3s ease;
}

.gap-bar__filled--high {
  background-color: #e53935;
}

.gap-bar__filled--medium {
  background-color: #fb8c00;
}

.gap-bar__filled--low {
  background-color: #43a047;
}

.gap-bar__label {
  min-width: 36px;
  text-align: right;
  font-weight: 500;
}

.products-cell {
  display: flex;
  flex-wrap: wrap;
  align-content: center;
  margin-top: 8px;
  margin-bottom: 8px;
}

.products-col {
  max-width: 300px;
  width: 300px;
}

.product-hidden {
  display: none !important;
}

.product-overflow-chip {
  cursor: pointer;
}
</style>
